import { useQuery } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { accessManagementQuery } from "../queries";

export interface UseAccessManagementOptions {
  /**
   * Whether to enable the query. Defaults to true.
   */
  enabled?: boolean;
  /**
   * Refetch interval in milliseconds. Set to false to disable.
   */
  refetchInterval?: number | false;
}

/**
 * Hook to fetch access management data (users and role assignments) for the current tenant.
 * Requires ApiConfigProvider to be set up in the component tree.
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useAccessManagement();
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <Error message={error.message} />;
 *
 * return (
 *   <UserList users={data.users} roles={data.roles} />
 * );
 * ```
 */
export function useAccessManagement(options: UseAccessManagementOptions = {}) {
  const apiConfig = useApiConfig();
  const { enabled = true, refetchInterval = false } = options;

  return useQuery({
    ...accessManagementQuery(apiConfig, {}),
    enabled,
    refetchInterval,
  });
}
